import {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { defaultEmptyFun, windowOpen } from "../../utils/helper";
import { Box, Button, Stack, Typography } from "@mui/material";
import OfferSection from "../CustomOfferSection/OfferSection";
import CommonGifAnimation from "../CustomGifAnimation/CommonGifAnimation";
import HealthSuccessGif from "../../assets/gif/Success.json";
import InfoOutlineIcon from "../../assets/icons/information-outline.svg";
import { Colors } from "../../styles/constant";
import useWindowSize from "../../hooks/useWindowSize";
import {
  tabViewPoint,
  ONE_LOGIN_DASHBOARD,
  nudgeBannerName,
} from "../../utils/constants";
import {
  E_ChannelOptions,
  T_AnimationData,
} from "../../globalTypes/GlobalTypes";
import { clearSsoSlice } from "../../features/Feature_SSO/Sso_Slice";
import { useAppSelector } from "../../redux-store/CustomReduxHooks";
import usePageMessages, {
  useHlSuccessMessages,
} from "../../hooks/useCmsPageMessages";
import CustomNetworkImage from "../CustomNetworkImage/CustomNetworkImage";
import { marked } from "marked";
import DOMPurify from "dompurify";
import "../../components/CustomSuccessPopupBody/CommonSuccessPopupBody.scss";
import useSuccessNudgeData from "../../hooks/useSuccessNudge";
import { T_NudgeBannerData } from "../../features/Features_Cms/CmsGlobal.Types";
import CustomNudge from "./CustomNudge";
import CustomSimpleBanner from "../CustomSimpleCard/CustomSimpleBanner";
import useLeadRedirectionHandler from "../../hooks/useLeadRedirectionHandler";
import LeadPopups from "../../pages/Home/RevampHomeComponents/LeadPopups/LeadPopups";
import SuccessAudio from "../SuccessAudio/SuccessAudio";

interface Props {
  title?: string;
  titleDark?: boolean;
  des?: ReactNode;
  btnLabel: string;
  btnOnClick?: any;
  stepValue?: number;
  btnDisabled?: any;
  withInfo?: boolean;
  withInfoText?: string;
  extraInfo?: any;
  subDescription?: ReactNode;
  additionalInfo?: boolean;
  additionalContent?: ReactElement;
  offerTitle?: string;
  cmsKey?: string;
  variable?: string;
  isChangesForLifeInsurance?: boolean;
  module?: string;
  subDesWithInfoIcon?: boolean;
  isChangesForMf?: boolean;
  forReKyc?: boolean;
  isPlaySuccessAudio?: boolean;
}

interface MarkedOptions {
  gfm?: boolean;
  breaks?: boolean;
  headerIds?: boolean;
  mangle?: boolean;
}

const markedOptions: MarkedOptions = {
  gfm: true,
  breaks: true,
  headerIds: true,
  mangle: false,
};

async function getMarkedVal(val: string): Promise<string> {
  const markedContent = await marked(val, markedOptions);
  return DOMPurify.sanitize(markedContent);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface HlSuccessMessagesProps {
  cmsKey: string;
  title?: string;
  titleDark: boolean;
  des?: ReactNode;
  subDescription?: ReactNode;
  subDesWithInfoIcon: boolean;
  variable?: string | null;
  extraInfo?: any;
  isChangesForLifeInsurance: boolean;
  isChangesForMf: boolean;
  withInfoText?: string | null;
  isPseudoLoggedIn: boolean;
  btnLabel: string;
  btnDisabled: any;
  handleClick: () => void;
  handleNudgeClick: (nudgeData: T_NudgeBannerData) => void;
  forReKyc: boolean;
}

const HlSuccessMessages: FC<HlSuccessMessagesProps> = ({
  cmsKey,
  title,
  titleDark,
  des,
  subDescription,
  subDesWithInfoIcon,
  variable,
  extraInfo,
  isChangesForLifeInsurance,
  isChangesForMf,
  withInfoText,
  isPseudoLoggedIn,
  btnLabel,
  btnDisabled,
  handleClick,
  handleNudgeClick,
  forReKyc,
}) => {
  const { width } = useWindowSize();
  const isDesktopView = width >= tabViewPoint;
  const hlMessages = useHlSuccessMessages(cmsKey);
  const nudgeData: T_NudgeBannerData | null = useSuccessNudgeData();
  const [extraInfoMarked, setExtraInfoMarked] = useState("");

  const initialUrl =
    nudgeData?.attributes?.infographic?.data?.attributes?.url ?? "";

  const updateExtraInfoMarked = useCallback(async (val: string) => {
    const sanitized = await getMarkedVal(val);
    setExtraInfoMarked(sanitized);
  }, []);

  useEffect(() => {
    if (hlMessages?.attributes.extraInfo) {
      updateExtraInfoMarked(hlMessages.attributes.extraInfo);
    }
  }, [hlMessages?.attributes.extraInfo, updateExtraInfoMarked]);

  return (
    <Box
      className={isChangesForMf ? "common-success-popup-body-hfl" : ""}
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          flex: 1,
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={"13.5625rem"}
        >
          {hlMessages?.attributes?.webImage?.data?.attributes.url ? (
            <CustomNetworkImage
              src={hlMessages.attributes.webImage.data.attributes.url}
              alt=""
            />
          ) : (
            <CommonGifAnimation
              width="16.875rem"
              animationData={HealthSuccessGif as T_AnimationData}
            />
          )}
        </Box>

        {(title || hlMessages?.attributes.title) && (
          <Typography
            textAlign="center"
            variant="h3"
            color={titleDark ? Colors.textblack : undefined}
            fontWeight={titleDark ? 600 : undefined}
            sx={{
              padding: isDesktopView ? " 0rem 3rem " : "0rem",
              textAlign: "center",
              mt: isDesktopView ? "1rem" : "0rem",
              mb: isDesktopView ? "1rem" : "0.5rem",
            }}
          >
            {title || hlMessages?.attributes.title}{" "}
          </Typography>
        )}

        {forReKyc && subDescription && (
          <Box
            sx={{
              background:
                "linear-gradient(90deg, rgba(255, 244, 217, 0.5) 0%, rgba(255, 244, 217, 0.25) 100%)",
              borderRadius: "4px",
              mx: "auto",
              width: "fit-content",
              border: "1px solid  #F1F3F6",
              padding: "0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            {subDescription}
          </Box>
        )}

        {des && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography
              marginTop=".5rem"
              variant="subtitle2"
              color={Colors.descriptionGray}
              textAlign="center"
            >
              {des ||
                (hlMessages?.attributes.description
                  ? hlMessages.attributes.description.replace(
                      "#variable",
                      variable ?? "",
                    )
                  : des)}
            </Typography>
          </Box>
        )}

        {subDescription && !forReKyc && (
          <Box
            sx={{
              backgroundColor: Colors.lightYellow,
              mt: ".5rem",
              borderRadius: "4px",
              mx: "auto",
              width: "fit-content",
              // maxWidth: isDesktopView ? "calc(100% - 16rem)" : "none",
              px: isDesktopView ? "7px" : 0,
            }}
          >
            <Typography
              variant="subtitle2"
              color={Colors.black}
              textAlign="center"
              padding={isDesktopView ? "12px 0px" : "8px 12px"}
              sx={{
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {subDescription}
            </Typography>
          </Box>
        )}

        {hlMessages?.attributes?.extraInfo ? (
          <Box
            sx={{
              paddingX: "2.25rem",
              paddingY: "0.625rem",
              fontSize: "14px",
              fontWeight: "700",
              color: Colors.descriptionGray,
              backgroundColor: "#F9FAFB",
            }}
            dangerouslySetInnerHTML={{ __html: extraInfoMarked }}
          />
        ) : (
          extraInfo
        )}

        {nudgeData?.attributes?.displayFullBannerImage ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "0.5rem",
            }}
          >
            <CustomSimpleBanner
              useAlternateBannerImage={
                nudgeData?.attributes?.fullBannerImage?.data?.attributes?.url ??
                ""
              }
              onClick={() => handleNudgeClick(nudgeData)}
              style={{ height: "7.1875rem" }}
            />
          </Box>
        ) : nudgeData?.attributes?.title ? (
          <CustomNudge
            nudgeData={nudgeData}
            isChangesForLifeInsurance={isChangesForLifeInsurance}
            initialUrl={initialUrl}
            withInfoText={withInfoText ?? null}
            handleNudgeClick={() => handleNudgeClick(nudgeData)}
          />
        ) : null}

        {hlMessages?.attributes?.linkLabel && (
          <Box>
            <Stack
              columnGap={0.5}
              direction="row"
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginBottom="2rem"
              marginTop="2rem"
              onClick={() => {
                window.location.href = hlMessages.attributes.link ?? "";
              }}
            >
              <Typography
                sx={{ cursor: "pointer" }}
                variant="h5"
                color={Colors.red}
              >
                {hlMessages?.attributes?.linkLabel}
              </Typography>
              <CustomNetworkImage
                src={
                  hlMessages?.attributes?.linkIcon?.data?.attributes.url ?? ""
                }
                alt=""
                style={{
                  height: "1.05rem",
                  marginLeft: ".25rem",
                  paddingTop: "0.25rem",
                  cursor: "pointer",
                }}
              />
            </Stack>
          </Box>
        )}
      </Box>
      <Box className="common-success-popup-body-hfl-cta">
        {!isPseudoLoggedIn && (
          <Button
            fullWidth
            variant="contained"
            className="popup-btn"
            onClick={handleClick}
            disabled={btnDisabled}
            sx={{ width: "100%" }}
          >
            {btnLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface DefaultPageMessageProps {
  cmsKey: string;
  title?: string;
  titleDark: boolean;
  des?: ReactNode;
  subDescription?: ReactNode;
  variable?: string | null;
  extraInfo?: any;
  offerTitle?: string;
  withInfo: boolean;
  additionalInfo: boolean;
  additionalContent?: ReactElement;
  isChangesForLifeInsurance: boolean;
  isChangesForMf: boolean;
  withInfoText?: string | null;
  isPseudoLoggedIn: boolean;
  btnLabel: string;
  btnDisabled: any;
  handleClick: () => void;
  handleNudgeClick: (nudgeData: T_NudgeBannerData) => void;
}

const DefaultPageMessage: FC<DefaultPageMessageProps> = ({
  cmsKey,
  title,
  titleDark,
  des,
  subDescription,
  variable,
  extraInfo,
  offerTitle,
  withInfo,
  additionalInfo,
  additionalContent,
  isChangesForLifeInsurance,
  isChangesForMf,
  withInfoText,
  isPseudoLoggedIn,
  btnLabel,
  btnDisabled,
  handleClick,
  handleNudgeClick,
}) => {
  const nudgeData: T_NudgeBannerData | null = useSuccessNudgeData();
  const pageMessages = usePageMessages(cmsKey);

  const initialUrl =
    nudgeData?.attributes?.infographic?.data?.attributes?.url ?? "";

  return (
    <>
      <Box className="common-success-popup-body">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="13.5625rem"
        >
          {pageMessages?.attributes?.webImage.data?.attributes.url ? (
            <CustomNetworkImage
              src={pageMessages.attributes.webImage.data.attributes.url}
              alt=""
            />
          ) : (
            <CommonGifAnimation
              width="16.875rem"
              animationData={HealthSuccessGif as T_AnimationData}
            />
          )}
        </Box>

        {(title || pageMessages?.attributes.title) && (
          <Typography
            textAlign="center"
            variant="h3"
            color={titleDark ? Colors.textblack : undefined}
            fontWeight={titleDark ? 600 : undefined}
          >
            {pageMessages?.attributes.title ?? title}
          </Typography>
        )}

        <Typography
          marginTop=".25rem"
          textAlign="center"
          variant="subtitle2"
          marginBottom=".5rem"
        >
          {cmsKey === "PM03"
            ? des
            : pageMessages?.attributes?.message
              ? pageMessages.attributes.message.replace(
                  "#variable",
                  variable ?? "",
                )
              : des}
        </Typography>

        {subDescription && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "1rem",
              padding: "0.563rem",
            }}
          >
            <img
              src={InfoOutlineIcon}
              style={{ marginRight: ".5rem" }}
              alt="Info icon"
            />
            <Typography
              textAlign="center"
              variant="h5"
              color="#767D93"
              width="13rem"
              marginBottom="1rem"
            >
              {subDescription}
            </Typography>
          </Box>
        )}

        {/* the below OfferSection is removed from sprint-1 as of now. When it is uncommented, remove marginBottom from above Typography */}
        {offerTitle ? <OfferSection title={offerTitle} /> : null}

        {withInfo ||
          (nudgeData?.attributes?.displayFullBannerImage ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: ".5rem",
              }}
            >
              <CustomSimpleBanner
                useAlternateBannerImage={
                  nudgeData?.attributes?.fullBannerImage?.data?.attributes
                    ?.url ?? ""
                }
                onClick={() => handleNudgeClick(nudgeData)}
                style={{ height: "7.1875rem" }}
              />
            </Box>
          ) : nudgeData?.attributes?.title ? (
            <CustomNudge
              nudgeData={nudgeData}
              isChangesForLifeInsurance={isChangesForLifeInsurance}
              initialUrl={initialUrl}
              withInfoText={withInfoText ?? null}
              handleNudgeClick={() => handleNudgeClick(nudgeData)}
            />
          ) : null)}

        {extraInfo}
        {additionalInfo && additionalContent}
      </Box>
      <Box className="common-success-popup-body-cta">
        {!isPseudoLoggedIn && (
          <Button
            fullWidth
            variant="contained"
            className="popup-btn"
            onClick={handleClick}
            disabled={btnDisabled}
            sx={{ marginBottom: isChangesForMf ? "1rem" : "" }}
          >
            {btnLabel}
          </Button>
        )}
      </Box>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const CommonSuccessPopupBody: FC<Props> = ({
  title,
  des,
  subDescription = null,
  btnLabel,
  btnOnClick = defaultEmptyFun,
  stepValue = null,
  btnDisabled = false,
  withInfo = false,
  titleDark = false,
  withInfoText = null,
  extraInfo = undefined,
  additionalInfo = false,
  additionalContent,
  offerTitle,
  cmsKey = "",
  variable = null,
  isChangesForLifeInsurance = false,
  module,
  subDesWithInfoIcon = false,
  isChangesForMf = false,
  forReKyc = false,
  isPlaySuccessAudio = false,
}) => {
  const csAppSlice = useAppSelector((st) => st.CsAppSlice);
  const isPseudoLoggedIn = useAppSelector(
    (st) => st.PseudoLoginSlice.isPseudoLoggedIn,
  );
  const {
    handleClick: handleNudgeClick,
    isLeadPopupsOpen,
    qrCode,
    leadIntent,
    buyJourney,
    leadIntentConfirmation,
    isLoading,
  } = useLeadRedirectionHandler(nudgeBannerName);

  const handleClick = useCallback(() => {
    if (stepValue === null) {
      if (csAppSlice.channel === E_ChannelOptions.ONE_ABC) {
        clearSsoSlice();
        windowOpen(ONE_LOGIN_DASHBOARD, "_self");
      } else {
        btnOnClick();
      }
    } else {
      btnOnClick(stepValue);
    }
  }, [stepValue, csAppSlice.channel, btnOnClick]);

  const sharedProps = {
    cmsKey,
    title,
    titleDark,
    des,
    subDescription,
    variable,
    extraInfo,
    isChangesForLifeInsurance,
    isChangesForMf,
    withInfoText: withInfoText ?? null,
    isPseudoLoggedIn,
    btnLabel,
    btnDisabled,
    handleClick,
    handleNudgeClick,
  };

  return (
    <>
      <SuccessAudio play={isPlaySuccessAudio} />
      {module === "HL" ? (
        <HlSuccessMessages
          {...sharedProps}
          subDesWithInfoIcon={subDesWithInfoIcon}
          forReKyc={forReKyc}
        />
      ) : (
        <DefaultPageMessage
          {...sharedProps}
          withInfo={withInfo}
          additionalInfo={additionalInfo}
          additionalContent={additionalContent}
          offerTitle={offerTitle}
        />
      )}
      {isLeadPopupsOpen && (
        <LeadPopups
          qrCode={qrCode}
          leadIntent={leadIntent}
          buyJourney={buyJourney}
          leadIntentConfirmation={leadIntentConfirmation}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default CommonSuccessPopupBody;
